export const LEGACY_CONSENT_STORAGE_KEY = 'consent'
export const CONSENT_STATUS_STORAGE_KEY = 'cookiesStatus'
export const CUSTOM_CONSENT_UPDATE_EVENT = 'custom_consent_update'

export type ConsentStatus = 'accepted' | 'refused'

type GoogleConsentValue = 'granted' | 'denied'
type GoogleConsentMode = 'default' | 'update'

const GOOGLE_ANALYTICS_SCRIPT_SELECTOR =
	"script[data-google-analytics-consent='managed']"

const parseLegacyConsentValue = (value: string | null): boolean | null => {
	if (value === null) {
		return null
	}

	try {
		return JSON.parse(value) === true
	} catch (error) {
		console.warn('Failed to parse legacy consent from localStorage:', error)
		return null
	}
}

const normalizeConsentStatus = (value: string | null): ConsentStatus | null => {
	const normalizedValue = String(value || '').toLowerCase()

	if (normalizedValue === 'accepted' || normalizedValue === 'refused') {
		return normalizedValue
	}

	return null
}

const getMeasurementId = (): string => {
	return process.env.GOOGLE_ANALYTICS_KEY || ''
}

const ensureDataLayer = (): any[] => {
	window.dataLayer = window.dataLayer || []
	return window.dataLayer
}

const ensureGtag = () => {
	ensureDataLayer()

	if (typeof window.gtag !== 'function') {
		window.gtag = function gtag() {
			window.dataLayer?.push(arguments)
		}
	}

	return window.gtag
}

const resolveGoogleConsentValue = (
	status: ConsentStatus
): GoogleConsentValue => {
	return status === 'accepted' ? 'granted' : 'denied'
}

const configureGoogleAnalytics = (): void => {
	const measurementId = getMeasurementId()

	if (!measurementId || typeof window === 'undefined') {
		return
	}

	const gtag = ensureGtag()

	if (window.googleAnalyticsConfiguredId === measurementId) {
		return
	}

	gtag('js', new Date())
	gtag('config', measurementId, { anonymize_ip: false })
	window.googleAnalyticsConfiguredId = measurementId
}

export const readStoredAnalyticsConsent = (): boolean | null => {
	if (typeof window === 'undefined') {
		return null
	}

	const storedStatus = normalizeConsentStatus(
		window.localStorage.getItem(CONSENT_STATUS_STORAGE_KEY)
	)

	if (storedStatus) {
		return storedStatus === 'accepted'
	}

	return parseLegacyConsentValue(
		window.localStorage.getItem(LEGACY_CONSENT_STORAGE_KEY)
	)
}

export const readConsentStatus = (): ConsentStatus => {
	return readStoredAnalyticsConsent() === true ? 'accepted' : 'refused'
}

export const hasAnalyticsConsent = (): boolean => {
	return readConsentStatus() === 'accepted'
}

export const persistConsentStatus = (status: ConsentStatus): boolean => {
	if (typeof window === 'undefined') {
		return false
	}

	const nextLegacyConsent = status === 'accepted'
	const previousStatus = normalizeConsentStatus(
		window.localStorage.getItem(CONSENT_STATUS_STORAGE_KEY)
	)
	const previousLegacyConsent = parseLegacyConsentValue(
		window.localStorage.getItem(LEGACY_CONSENT_STORAGE_KEY)
	)

	window.localStorage.setItem(CONSENT_STATUS_STORAGE_KEY, status)
	window.localStorage.setItem(
		LEGACY_CONSENT_STORAGE_KEY,
		JSON.stringify(nextLegacyConsent)
	)

	return (
		previousStatus !== status || previousLegacyConsent !== nextLegacyConsent
	)
}

export const buildGoogleConsentState = (
	status: ConsentStatus
): Record<string, GoogleConsentValue | 'granted'> => {
	const value = resolveGoogleConsentValue(status)

	return {
		ad_storage: value,
		ad_user_data: value,
		ad_personalization: value,
		analytics_storage: value,
		functionality_storage: value,
		personalization_storage: value,
		security_storage: 'granted'
	}
}

export const applyGoogleConsentMode = (
	status: ConsentStatus,
	mode: GoogleConsentMode = 'update'
): void => {
	if (typeof window === 'undefined') {
		return
	}

	const gtag = ensureGtag()
	gtag('consent', mode, buildGoogleConsentState(status))

	window.dataLayer?.push({
		event: CUSTOM_CONSENT_UPDATE_EVENT,
		source: 'custom'
	})
}

export const syncGoogleAnalyticsScript = (): void => {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return
	}

	const measurementId = getMeasurementId()

	if (!measurementId) {
		return
	}

	const existingScript = document.querySelector(
		GOOGLE_ANALYTICS_SCRIPT_SELECTOR
	) as HTMLScriptElement | null

	// No need to validate consent here, as the script should be loaded regardless of consent status.
	// if (!hasAnalyticsConsent()) {
	// 	if (existingScript) {
	// 		existingScript.remove()
	// 	}

	// 	delete window.googleAnalyticsConfiguredId
	// 	return
	// }

	if (existingScript) {
		configureGoogleAnalytics()
		return
	}

	const script = document.createElement('script')
	script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
	script.async = true
	script.dataset.googleAnalyticsConsent = 'managed'

	script.onload = () => {
		configureGoogleAnalytics()
	}

	script.onerror = () => {
		console.error('Failed to load Google Analytics script.')
	}

	document.head.appendChild(script)
}

declare global {
	interface Window {
		dataLayer?: any[]
		gtag?: (...args: any[]) => void
		googleAnalyticsConfiguredId?: string
	}
}